import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useCreateTicket, useUsers } from '@/hooks/api-hooks';
import { useState, useEffect } from 'react';
import { Button, Select } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { Ticket, TICKET_STATUSES, TICKET_PRIORITIES } from '@turizi/shared';
import { TICKETS_PATH } from '@/lib/navigation';
import TicketEditor from '@/components/ticket';

interface Props {
    onCreate?: () => void;
}

export default function ModalNewTicket({ onCreate }: Props): JSX.Element {
    const { t } = useTranslation();

    const { data: usersResponse } = useUsers();
    const createMutation = useCreateTicket();

    const users = usersResponse?.data || [];
    const [shouldAutoFocus, setShouldAutoFocus] = useState(false);
    const [createdTicketId, setCreatedTicketId] = useState<number | null>(null);

    const [formData, setFormData] = useState<Partial<Ticket>>({
        title: '',
        content: '',
        status: 'pending',
        priority: 'medium',
        assignee_id: null,
        due_datetime: null,
    });

    // Trigger autofocus after modal is visible
    useEffect(() => {
        const timer = setTimeout(() => {
            setShouldAutoFocus(true);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    const handleSave = async () => {
        try {
            const result = await createMutation.mutateAsync(formData);
            if (result?.data?.id) {
                setCreatedTicketId(result.data.id);
            }
        } catch (error) {
            console.error('Error saving ticket:', error);
        }
    };

    const userOptions = users.map(user => ({
        value: String(user.id),
        label: user.name || user.email || `#${user.id}`,
    }));

    const statusOptions = TICKET_STATUSES.map(status => ({
        value: status,
        label: t(`status_${status}`),
    }));

    const priorityOptions = TICKET_PRIORITIES.map(priority => ({
        value: priority,
        label: t(`priority_${priority}`),
    }));

    return (
        <div className="">
            <div className="grid grid-cols-[1fr_300px] gap-2">
                {/* Left Column - Editor */}
                <div className="w-120">
                    <TicketEditor
                        title={formData.title || ''}
                        content={formData.content || ''}
                        onTitleChange={(title) => setFormData({ ...formData, title })}
                        onContentChange={(content) => setFormData({ ...formData, content })}
                        titlePlaceholder={t('ticket_title_placeholder')}
                        contentPlaceholder={t('ticket_content_placeholder')}
                        autoFocus={shouldAutoFocus}
                    />
                </div>

                {/* Right Column - Form Fields */}
                <div className="space-y-4">
                    <div className=" space-y-4">
                        <Select
                            label={t('status')}
                            data={statusOptions}
                            value={formData.status}
                            onChange={(value) => setFormData({ ...formData, status: value as Ticket['status'] })}
                        />

                        <Select
                            label={t('priority')}
                            data={priorityOptions}
                            value={formData.priority}
                            onChange={(value) => setFormData({ ...formData, priority: value as Ticket['priority'] })}
                        />

                        <Select
                            label={t('assignee')}
                            data={userOptions}
                            value={formData.assignee_id ? String(formData.assignee_id) : null}
                            onChange={(value) => setFormData({ ...formData, assignee_id: value ? Number(value) : null })}
                            clearable
                            searchable
                        />

                        <DateTimePicker
                            highlightToday={true}
                            label={t('due_date')}
                            value={formData.due_datetime ? new Date(formData.due_datetime) : null}
                            onChange={(value) => setFormData({
                                ...formData,
                                due_datetime: value ? new Date(value) : null
                            })}
                            clearable
                            radius="md"
                        />
                    </div>

                    {/* Action Button */}
                    <div className="">
                        {createdTicketId ? (
                            <Link to={`${TICKETS_PATH}/${createdTicketId}`}>
                                <Button
                                    onClick={onCreate}
                                    color="black"
                                    radius="xl"
                                    size="sm"
                                    fullWidth
                                    variant="outline"
                                >
                                    {t('view_ticket')}
                                </Button>
                            </Link>
                        ) : (
                            <Button
                                color="black"
                                radius="xl"
                                size="sm"
                                fullWidth
                                onClick={handleSave}
                                loading={createMutation.isPending}
                                disabled={!formData.title}
                            >
                                {t('save')}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
